import { makeid } from 'utils/helpers/makeid';

import {
  additionalFollowUpItem,
  freeTextCommentsItem,
  isPatientCROorDNCItem,
  needEscalateItem,
  twoWaySyncInfoItem,
  uniqueDaysItem,
} from '../common';

import type { TFormItem } from '../../types';

export const spo2: TFormItem[] = [
  {
    key: makeid(10),
    title: <p>Is the SpO2 device functioning properly?</p>,
    type: 'radio',
    required: false,
    name: 'spo2DeviceFunctioningProperly',
    options: [
      {
        label: 'Yes',
        value: true,
        key: makeid(10),
      },
      {
        label: 'No (describe below; provide patient assistance if possible)',
        value: false,
        key: makeid(10),
      },
    ],
    items: [
      {
        type: 'textarea',
        name: 'spo2DeviceFunctioningProperlyComment',
        title: null,
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Did the patient report any difficulties with SpO2 or pulse readings
        (device error, placement trouble, battery, etc.)?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'didPatientReportSpO2OrPulse',
    options: [
      {
        label: 'Yes (describe below; provide patient assistance if possible)',
        value: true,
        key: makeid(10),
      },
      {
        label: 'No',
        value: false,
        key: makeid(10),
      },
    ],
    items: [
      {
        type: 'textarea',
        name: 'didPatientReportSpO2OrPulseComment',
        title: null,
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: null,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Number of at-risk SpO2 readings reviewed: <span>__</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'atRiskNumber',
        validation: {
          min: 0,
        },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest SpO2 value: <span>__ %</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestSpO2',
        validation: {
          min: 0,
        },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest SpO2 value: <span>__ %</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestSpO2',
        validation: {
          min: 0,
        },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestPulse',
        validation: {
          min: 0,
        },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest pulse value: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestPulse',
        validation: {
          min: 0,
        },
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>What type of at-risk trend(s) were observed?</p>,
    type: 'radio',
    name: 'typeofTrend',
    options: [
      {
        label: 'High readings',
        value: 'high',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'checkbox',
            name: 'highTrends',
            title: null,
            key: makeid(10),
            options: [
              { label: 'SpO2', value: 'SpO2', key: makeid(10) },
              { label: 'Pulse', value: 'pulse', key: makeid(10) },
            ],
          },
        ],
      },
      {
        label: 'Low readings',
        value: 'low',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'checkbox',
            name: 'lowTrends',
            title: null,
            key: makeid(10),
            options: [
              { label: 'SpO2', value: 'SpO2', key: makeid(10) },
              {
                label: 'Pulse',
                value: 'pulse',
                key: makeid(10),
              },
            ],
          },
        ],
      },
      {
        label: 'No significant trends',
        value: 'none',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Compared to the last review, have the total number of at-risk
            readings:
          </p>
        ),
        type: 'radio',
        required: false,
        name: 'comparedToLastReview',
        options: [
          { label: 'Increased', value: 'increased', key: makeid(10) },
          { label: 'Decreased', value: 'decreased', key: makeid(10) },
          {
            label: 'Remained about the same',
            value: 'remained about the same',
            key: makeid(10),
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Has the number or trend of at-risk readings changed compared to the last
        review?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'comparedToLastReviewTrend',
    options: [
      { label: 'Trend improving', value: 'improving', key: makeid(10) },
      { label: 'Trend worsening', value: 'worsening', key: makeid(10) },
      {
        label: 'No significant change',
        value: 'about the same',
        key: makeid(10),
      },
    ],
  },
  { ...uniqueDaysItem('SpO2') },
  { ...twoWaySyncInfoItem },
  { ...isPatientCROorDNCItem },
  { ...additionalFollowUpItem },
  { ...needEscalateItem },
  { ...freeTextCommentsItem },
];
